import React, { useMemo } from "react";
import { tplThemeVarsForWebsiteLoader } from "../../Screens/DashboardView/MasterTemplateEditor/templateThemeVars";
import type { Tweaks } from "../../Screens/DashboardView/MasterTemplateEditor/types";
import "./browsableBundlingLoaders.css";

export interface TemplateBundlingThumbnailLoaderProps {
  /** `public` — `/website/:user` & read-only preview. `embedded` — dashboard Website tab fallback. `embedded-preview` — inside editor chrome (centered @handle in the preview pane). */
  variant?: "public" | "embedded" | "embedded-preview";
  username?: string;
  /** When false, only the animated rule shows (handle lives in BrowserChrome URL bar). */
  showHandle?: boolean;
  /** Optional bottom-right pill text (hidden when empty). */
  statusText?: string;
  /** Saved template preset / palette / fonts — matches the site once `TemplatePage` mounts. */
  tweaks?: Partial<Tweaks> | null;
  device?: "desktop" | "tablet" | "mobile";
  /**
   * When false, hold neutral chrome until `template_settings` has loaded — avoids flashing
   * the default Atelier palette before the creator's saved preset.
   */
  settingsThemeReady?: boolean;
}

function normalizeHandle(raw?: string): string | undefined {
  const s = raw?.trim();
  if (!s) return undefined;
  return s.replace(/^@+/, "");
}

function resolveLoaderHandle(username?: string): string | undefined {
  const fromProp = normalizeHandle(username);
  if (fromProp) return fromProp;
  try {
    const logged =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("loggedIn")
        : null;
    return normalizeHandle(logged ?? undefined);
  } catch {
    return undefined;
  }
}

function BundlingHandleMark({ username }: { username?: string }) {
  const handle = resolveLoaderHandle(username);
  const line = handle ? `@${handle}` : "@username";
  const long = line.length > 28;
  return (
    <div className="bbl-template-mark">
      <span className={`bbl-template-at${long ? " bbl-template-at--long" : ""}`}>{line}</span>
      <span className="bbl-template-rule" aria-hidden />
    </div>
  );
}

/**
 * Website template initial loader — `@handle` + rule; themed once saved settings are known.
 */
export function TemplateBundlingThumbnailLoader({
  variant = "public",
  username,
  showHandle = true,
  statusText = "",
  tweaks,
  device = "desktop",
  settingsThemeReady = true,
}: TemplateBundlingThumbnailLoaderProps) {
  const themeStyle = useMemo(
    () =>
      settingsThemeReady ? tplThemeVarsForWebsiteLoader(tweaks, device) : undefined,
    [settingsThemeReady, tweaks, device],
  );
  const rootClass = [
    variant === "public"
      ? "bbl-root bbl-root--public"
      : variant === "embedded-preview"
        ? "bbl-root bbl-root--embedded-preview"
        : "bbl-root bbl-root--embedded",
    settingsThemeReady ? "" : "bbl-root--theme-pending",
  ]
    .filter(Boolean)
    .join(" ");
  const handle = normalizeHandle(username);
  const aria = handle ? `Loading template for @${handle}` : "Loading template";

  return (
    <div
      className={rootClass}
      style={themeStyle}
      data-tpl-font-pair={
        settingsThemeReady ? tweaks?.fontPair ?? "Editorial" : undefined
      }
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={aria}
    >
      <div
        className={[
          "bbl-stage",
          "bbl-stage--template",
          settingsThemeReady ? "" : "bbl-stage--theme-pending",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {variant === "public" && !settingsThemeReady ? null : showHandle ? (
          <BundlingHandleMark username={username} />
        ) : (
          <span className="bbl-template-rule" aria-hidden />
        )}
      </div>
      {statusText && settingsThemeReady ? (
        <div className="bbl-status">{statusText}</div>
      ) : null}
    </div>
  );
}

/**
 * Initial dashboard shell loader — same typography + animated rule as template loader.
 */
export function DashboardBundlingThumbnailLoader({
  username,
  statusText = "Loading…",
  tweaks,
  device = "desktop",
  settingsThemeReady = true,
}: {
  username?: string;
  statusText?: string;
  tweaks?: Partial<Tweaks> | null;
  device?: "desktop" | "tablet" | "mobile";
  settingsThemeReady?: boolean;
}) {
  const themeStyle = useMemo(
    () =>
      settingsThemeReady ? tplThemeVarsForWebsiteLoader(tweaks, device) : undefined,
    [settingsThemeReady, tweaks, device],
  );
  const handle = normalizeHandle(username);
  const aria = handle ? `Loading dashboard for @${handle}` : "Loading dashboard";

  return (
    <div
      className={[
        "bbl-root",
        "bbl-root--dashboard",
        settingsThemeReady ? "" : "bbl-root--theme-pending",
      ]
        .filter(Boolean)
        .join(" ")}
      style={themeStyle}
      data-tpl-font-pair={
        settingsThemeReady ? tweaks?.fontPair ?? "Editorial" : undefined
      }
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={aria}
    >
      <div
        className={[
          "bbl-stage",
          "bbl-stage--template",
          settingsThemeReady ? "" : "bbl-stage--theme-pending",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <BundlingHandleMark username={username} />
      </div>
      {settingsThemeReady ? <div className="bbl-status">{statusText}</div> : null}
    </div>
  );
}
